import { initCenterElementOnItself } from '@/infrastructure/engine2d/helpers/FabricPositionHelper';
import { FONT_SIZE_MULTIPLIER } from '@/infrastructure/engine2d/engine2dConstants';
import { StaticCanvas, type CompleteTextStyleDeclaration } from 'fabric';
import type { IFabricTextCustom } from '@/infrastructure/engine2d/types/IFabricTextCustom';
import type { ITextboxCustom } from '@/infrastructure/engine2d/types/ITextboxCustom';
import type { ETextAlign } from '@/domain/textbox/ETextAlign';

type TFText = IFabricTextCustom | ITextboxCustom;

function updateFontFamily({
    alignCenter = true,
    fabricCanvas,
    fontFamily,
    fText,
    initial = false,
    selectedTextIndexes,
}: {
    alignCenter?: boolean;
    fabricCanvas: StaticCanvas;
    fontFamily: string;
    fText: TFText;
    initial?: boolean;
    selectedTextIndexes?: { end: number; start: number };
}): TFText {
    if (selectedTextIndexes) {
        fText.setSelectionStyles({ fontFamily: fontFamily }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        if (!initial) {
            removeCharacterLevelStyling(fText, 'fontFamily');
        }

        fText.set('fontFamily', fontFamily);
    }
    const centerPosition = initCenterElementOnItself(fText);

    if (alignCenter) {
        centerPosition(fText);
    }
    fText.set('dirty', true);
    fabricCanvas.renderAll();
    fText.setCoords();
    return fText;
}

function updateFontSize({
    alignCenter = true,
    fabricCanvas,
    fontSize,
    fText,
    selectedTextIndexes,
}: {
    alignCenter?: boolean;
    fabricCanvas: StaticCanvas;
    fontSize: number;
    fText: TFText;
    selectedTextIndexes?: { end: number; start: number };
}): TFText {
    if (selectedTextIndexes) {
        fText.setSelectionStyles({ fontSize: fontSize * FONT_SIZE_MULTIPLIER }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        removeCharacterLevelStyling(fText, 'fontSize');

        fText.set('fontSize', fontSize * FONT_SIZE_MULTIPLIER);
    }
    const centerPosition = initCenterElementOnItself(fText);
    if (alignCenter) {
        centerPosition(fText);
    }

    fText.set('dirty', true);

    fabricCanvas.renderAll();
    fText.setCoords();
    return fText;
}

// Update font color (hex string).
function updateFontColor({
    fabricCanvas,
    fontColor,
    fText,
    selectedTextIndexes,
}: {
    fabricCanvas: StaticCanvas;
    fontColor: string;
    fText: TFText;
    selectedTextIndexes?: { end: number; start: number };
}): TFText {
    if (selectedTextIndexes) {
        fText.setSelectionStyles({ fill: fontColor }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        removeCharacterLevelStyling(fText, 'fill');
        fText.set('fill', fontColor);
    }
    fText.set('dirty', true);
    fabricCanvas.renderAll();

    return fText;
}

function updateFontWeight({
    alignCenter = true,
    fabricCanvas,
    fontWeight = '300',
    fText,
    selectedTextIndexes,
}: {
    alignCenter?: boolean;
    fabricCanvas: StaticCanvas;
    fontWeight: '300' | '700';
    fText: TFText;
    selectedTextIndexes?: { end: number; start: number };
}): TFText {
    const centerPosition = initCenterElementOnItself(fText);

    if (selectedTextIndexes) {
        fText.setSelectionStyles({ fontWeight: fontWeight }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        removeCharacterLevelStyling(fText, 'fontWeight');
        fText.set('fontWeight', fontWeight);
    }
    fText.set('dirty', true);
    if (alignCenter) {
        centerPosition(fText);
    }
    fabricCanvas.renderAll();
    fText.setCoords();

    return fText;
}

function updateFontStyle({
    alignCenter = true,
    fabricCanvas,
    fontStyle = 'normal',
    fText,
    selectedTextIndexes,
}: {
    alignCenter?: boolean;
    fabricCanvas: StaticCanvas;
    fontStyle: 'italic' | 'normal';
    fText: TFText;
    selectedTextIndexes?: { end: number; start: number };
}): TFText {
    const centerPosition = initCenterElementOnItself(fText);

    if (selectedTextIndexes) {
        fText.setSelectionStyles({ fontStyle: fontStyle }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        removeCharacterLevelStyling(fText, 'fontStyle');
        fText.set('fontStyle', fontStyle);
    }

    if (alignCenter) {
        centerPosition(fText);
    }
    fText.set('dirty', true);
    fabricCanvas.renderAll();
    fText.setCoords();

    return fText;
}

function updateUnderline({
    fabricCanvas,
    fText,
    selectedTextIndexes,
    underline,
}: {
    fabricCanvas: StaticCanvas;
    fText: TFText;
    selectedTextIndexes?: { end: number; start: number };
    underline: boolean;
}): TFText {
    if (selectedTextIndexes) {
        fText.setSelectionStyles({ underline, textDecorationThickness: 40 }, selectedTextIndexes.start, selectedTextIndexes.end);
    } else {
        removeCharacterLevelStyling(fText, 'underline');
        fText.set('underline', underline);
        fText.set('textDecorationThickness', 40); // TODO: you should have a number between 0 and 100 that depends on the font size, default 66
    }

    fText.set('dirty', true);
    fabricCanvas.renderAll();
    return fText;
}

function updateAngle(fabricCanvas: StaticCanvas, fText: TFText, angle: number) {
    fText.rotate(angle);
    fabricCanvas.renderAll();
    fText.setCoords();
}

function updateAlignment(fabricCanvas: StaticCanvas, fText: TFText, newAlignement: ETextAlign) {
    const { textAlign } = fText;
    fText.set('textAlign', newAlignement ?? textAlign);
    fText.set('dirty', true);
    fabricCanvas.renderAll();
}

function updateLineHeight(fabricCanvas: StaticCanvas, fText: TFText, newLineSpacing: number) {
    const lineSpacingOffset = 16 / (FONT_SIZE_MULTIPLIER * 4);
    const refineLineSpacingOffset = (newLineSpacing === 0 ? 0.01 : newLineSpacing / (FONT_SIZE_MULTIPLIER * 4)) + lineSpacingOffset;
    fText.set('lineHeight', refineLineSpacingOffset === 0 ? 0.001 : refineLineSpacingOffset);
    fText.set('dirty', true);
    fabricCanvas.renderAll();
}

function updateCharSpacing(fabricCanvas: StaticCanvas, fText: TFText, newCharSpacing: number) {
    fText.set('charSpacing', newCharSpacing * FONT_SIZE_MULTIPLIER * 4);
    fText.set('dirty', true);
    fabricCanvas.renderAll();
}

function removeCharacterLevelStyling(fText: TFText, key: keyof CompleteTextStyleDeclaration) {
    if (fText.styles) {
        Object.entries(fText.styles).forEach(([line, chars]) => {
            Object.entries(chars).forEach(([char, style]) => {
                if (key in style) {
                    delete style[key];

                    // Clean up empty style objects
                    if (Object.keys(style).length === 0) {
                        delete chars[char];
                    }
                }
            });

            // Clean up empty line style arrays
            if (Object.keys(chars).length === 0) {
                delete fText.styles[line];
            }
        });
    }

    return fText;
}
export {
    updateAlignment,
    updateAngle,
    updateCharSpacing,
    updateFontColor,
    updateFontFamily,
    updateFontSize,
    updateFontStyle,
    updateFontWeight,
    updateLineHeight,
    updateUnderline,
};
